import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";

export default function Workflow() {
  const { readOnly } = useOutletContext();
  const [matrix, setMatrix] = useState({ statuses: [], transitions: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/workflow/transitions");
      setMatrix(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const findCell = (from, to) =>
    matrix.transitions.find((t) => t.from_status === from && t.to_status === to);

  const toggle = async (from, to, allowed) => {
    if (readOnly) return;
    const cell = findCell(from, to);
    if (cell?.invariant) return;
    try {
      await api.patch("/admin/workflow/transitions", {
        from_status: from,
        to_status: to,
        allowed,
      });
      toast.success(`${from} → ${to} ${allowed ? "enabled" : "disabled"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-workflow-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Workflow"
        title="Status workflow"
        description="Toggle which case transitions are allowed. Diagonal-hatched cells are policy invariants and cannot be changed."
      />

      <div className="brand-card overflow-x-auto p-6">
        {loading ? (
          <div className="space-y-2">
            <div className="skeleton-shimmer h-4 w-40" />
            <div className="skeleton-shimmer h-72 w-full" />
          </div>
        ) : (
          <TooltipProvider delayDuration={150}>
            <table className="min-w-full border-collapse text-sm">
              <caption className="sr-only">Status transition matrix</caption>
              <thead>
                <tr>
                  <th
                    className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.10em]"
                    style={{
                      color: "var(--page-fg-secondary)",
                      borderBottom: "1px solid var(--border-subtle)",
                      backgroundColor: "var(--surface-2)",
                    }}
                    scope="col"
                  >
                    From ↓ To →
                  </th>
                  {matrix.statuses.map((s) => (
                    <th
                      key={s}
                      className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em]"
                      style={{
                        color: "var(--page-fg)",
                        borderBottom: "1px solid var(--border-subtle)",
                        backgroundColor: "var(--surface-2)",
                      }}
                      scope="col"
                    >
                      {s}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.statuses.map((from) => (
                  <tr key={from}>
                    <th
                      className="px-4 py-4 text-left text-[12px] font-semibold"
                      style={{
                        color: "var(--page-fg)",
                        borderBottom: "1px solid var(--border-subtle)",
                        backgroundColor: "var(--surface-2)",
                      }}
                      scope="row"
                    >
                      {from}
                    </th>
                    {matrix.statuses.map((to) => {
                      // Self-cell — render as subtle hatch + dash
                      if (from === to) {
                        return (
                          <td
                            key={to}
                            className="hatch-invariant px-4 py-4 text-center"
                            style={{
                              borderBottom: "1px solid var(--border-subtle)",
                              color: "var(--page-fg-muted)",
                            }}
                          >
                            <span className="text-[11px]">—</span>
                          </td>
                        );
                      }
                      const cell = findCell(from, to);
                      if (!cell) return null;
                      const isInvariant = !!cell.invariant;
                      return (
                        <td
                          key={to}
                          className={`px-4 py-4 text-center ${
                            isInvariant ? "hatch-invariant" : ""
                          }`}
                          style={{
                            borderBottom: "1px solid var(--border-subtle)",
                          }}
                          data-testid={`workflow-cell-${from}-to-${to}`}
                        >
                          {isInvariant ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`workflow-invariant-${from}-to-${to}`}
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium uppercase tracking-[0.10em]"
                                  style={{
                                    color: "var(--page-fg-secondary)",
                                    border: "1px solid var(--border-subtle)",
                                    backgroundColor: "var(--surface)",
                                  }}
                                >
                                  <Lock className="h-3 w-3" strokeWidth={1.8} />
                                  invariant
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs text-xs"
                              >
                                {cell.invariant_reason ||
                                  "This transition is enforced by policy and cannot be changed."}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <Switch
                                checked={cell.allowed}
                                onCheckedChange={(v) => toggle(from, to, v)}
                                disabled={readOnly}
                                aria-label={`Transition ${from} to ${to}`}
                              />
                              {to === "Closed" &&
                                cell.closure_validation_invariant && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className="cursor-help text-[10px] uppercase tracking-[0.10em]"
                                        style={{ color: "var(--page-fg-muted)" }}
                                      >
                                        closure rules
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      {cell.closure_validation_reason ||
                                        "Closure validation always applies for transitions to Closed."}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
